import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import {
    HTMLAttributes, useCallback, useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import ReplayStep from '../ReplayStep/ReplayStep';
import styles from './ReplaySidebar.scss';

import type { RootState } from '~/store';
import type Api from '~/types';

import { DWH_EXPORT_EVENTS, HEIGHT_PLAYER, isShowPlayer } from '~/constants';
import dwhExport from '~/dwhExport';

import {
    hideRepeatSidebar,
    repeatBuildingProcess,
    setPauseAnimation,
    setTargetLevel,
} from '~/store/app';
import { useAppDispatch, useAppSelector } from '~/hooks';
import ClientApi from '~/clientApi';
import timer from '~/utils/timer';
import SpringScrollbars from '~/Components/SpringScrollbars/SpringScrollbars';
import PlayerControl from '~/Components/ReplaySidebar/PlayerControl';


const SCROLL_ID = 'replay-sidebar-scroll';
const TIMER_ID = 'replay-sidebar-timer-id';

const updateScrollDelay = 500;

export interface ReplaySidebarState {
    shipLevels: Array<Api.ShipLevel>,
    animationShipLevel: number,
    isRepeating: boolean,
    shipLevel: number,
    progress: number,
    isRepeatSidebarVisible: boolean,
    animationShipLevelIsInstant: boolean,
    isBlueprintsViewerVisible: boolean,
    isAnimationInProgress: boolean,
}

interface OnUpdateData {
    top: number,
}

const stateSelector = (state: RootState): ReplaySidebarState => {
    return {
        shipLevels: state.app.shipLevels,
        animationShipLevel: state.app.animationShipLevel,
        isRepeating: state.app.isRepeating,
        shipLevel: state.app.shipLevel,
        progress: state.app.account.progress as number,
        isRepeatSidebarVisible: state.app.isRepeatSidebarVisible,
        animationShipLevelIsInstant: state.app.animationShipLevelIsInstant,
        isBlueprintsViewerVisible: state.dialogs.isBlueprintsViewerVisible,
        isAnimationInProgress: state.app.isAnimationInProgress,
    };
};

const ReplaySidebar = () => {
    const state = useAppSelector(stateSelector, equal);
    const {
        shipLevels,
        animationShipLevel,
        isRepeating,
        shipLevel,
        isRepeatSidebarVisible,
        animationShipLevelIsInstant,
        isBlueprintsViewerVisible,
        isAnimationInProgress,
        progress,
    } = state;
    const [localShipLevel, setLocalShipLevel] = useState<number>(0);
    const isMouseInside = useRef<boolean>(false);
    const tempShipLevel = useRef<number>(0);
    const scrollbar = useRef<SpringScrollbars>();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const getScrollTop = useCallback((): number => {
        let level = localShipLevel;

        if (isRepeating || isAnimationInProgress) {
            level = animationShipLevel;
        }

        if (shipLevels.length > 0 && shipLevels[level - 1]) {
            const id = shipLevels[level - 1].name;
            const step = document.getElementById(id as string);
            const offsetTop = step ? step.offsetTop : 0;

            return offsetTop;
        }
        return 0;
    }, [animationShipLevel, isAnimationInProgress, isRepeating, localShipLevel, shipLevels]);

    const updateScrollPosition = useCallback(() => {
        if (scrollbar.current && !isMouseInside.current && !timer.isRunning(TIMER_ID)) {
            timer.start(TIMER_ID, 500, () => {
                window.requestAnimFrame(() => {
                    if (isRepeatSidebarVisible) {
                        const scrollTop = getScrollTop();
                        if (scrollbar.current) {
                            scrollbar.current.scrollTop(scrollTop);
                        }
                    }
                });
            });
        }
    }, [getScrollTop, isRepeatSidebarVisible]);

    useEffect(() => {
        updateScrollPosition();
    }, [isRepeatSidebarVisible, animationShipLevel, updateScrollPosition]);

    setTimeout(() => {
        if (tempShipLevel.current === shipLevel && localShipLevel !== shipLevel) {
            setLocalShipLevel(shipLevel);
        }
    }, 300);
    tempShipLevel.current = shipLevel;

    const updateOverlayMask = (opacity?: number) => {
        const min = 0.8;
        const max = 1;
        const alpha = opacity === undefined ? 0 : opacity;
        const relativeAlpha = alpha > min ? ((opacity || 0) - min) / (max - min) : 0;
        const scrollDiv = document.getElementById(SCROLL_ID);
        if (scrollDiv) {
            scrollDiv.style.webkitMaskImage = `-webkit-gradient(linear, left top, left bottom, color-stop(0%, rgba(0,0,0,1)), color-stop(80%, rgba(0,0,0,1)),  color-stop(100%, rgba(0, 0, 0, ${relativeAlpha})))`;
            scrollDiv.style.height = `calc(100% - 45px - ${isShowPlayer ? HEIGHT_PLAYER : 0}px)`;
        }
    };

    const onUpdate = useCallback((data: OnUpdateData) => {
        updateOverlayMask(data.top);
    }, []);

    const onStepClick = (shipLevel: Api.ShipLevel) => () => {
        if (shipLevel.level === animationShipLevel || shipLevel.level === animationShipLevel + 1) {
            return;
        }
        if (isAnimationInProgress && !isRepeating) {
            return;
        }
        dispatch(setPauseAnimation(false));
        dispatch(setTargetLevel({ level: progress }));
        dispatch(repeatBuildingProcess(Number(shipLevel.level)));
        dwhExport.send(DWH_EXPORT_EVENTS.REPEAT_ANIMATION_FROM_LEVEL, {
            level: Number(shipLevel.level),
        });
    };

    const renderSteps = () => {
        return shipLevels.map((ship, index) => {
            const isPlaying: boolean = ship.level === animationShipLevel
                && (isRepeating || isAnimationInProgress)
                && !animationShipLevelIsInstant;
            const isCurrent: boolean = localShipLevel === ship.level && !isRepeating;
            const isDisabled = (ship.level || 0) > localShipLevel || localShipLevel === 0;
            return (
                <ReplayStep
                    id={ship.name as string}
                    key={`ReplayStep${index}`}
                    index={ship.level as number}
                    count={shipLevels.length - 1}
                    descr={ship.description as string}
                    isPlaying={isPlaying}
                    onClick={onStepClick(ship)}
                    isDisabled={isDisabled}
                    isCurrent={isCurrent}
                    isAnimationInProgress={isAnimationInProgress}
                    animationShipLevel={animationShipLevel}
                />
            );
        });
    };

    const setScrollbarRef = useCallback((r: SpringScrollbars) => {
        scrollbar.current = r;
        updateOverlayMask();
    }, []);

    const onMouseEnter = useCallback(() => {
        timer.clear(TIMER_ID);
        isMouseInside.current = true;
        ClientApi.setScrollover(true);
    }, []);

    const onMouseLeave = useCallback(() => {
        isMouseInside.current = false;
        if (!isBlueprintsViewerVisible) {
            ClientApi.setScrollover(false);
        }
        setTimeout(() => {
            updateScrollPosition();
        }, updateScrollDelay);
    }, [isBlueprintsViewerVisible, updateScrollPosition]);

    const onCloseClick = useCallback(() => {
        playButtonClickSound();
        if (!isBlueprintsViewerVisible) {
            ClientApi.setScrollover(false);
        }
        dispatch(hideRepeatSidebar());
    }, [dispatch, isBlueprintsViewerVisible]);

    const getRenderedView = useCallback((props: HTMLAttributes<unknown>) => {
        return <div {...props} style={{ ...props.style, overflowX: 'hidden', paddingBottom: 11 }} />;
    }, []);

    const wrapper = classnames(styles.wrapper, {
        [styles.visible]: isRepeatSidebarVisible,
    });

    return (
        <div className={wrapper} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div className={styles.header}>
                {t('Все этапы строительства корабля')}
                <div className={styles.close} onClick={onCloseClick} />
            </div>
            {isShowPlayer && (
                <PlayerControl
                    animationShipLevel={animationShipLevel}
                    progress={progress}
                    isAnimationInProgress={isAnimationInProgress}
                    isRepeating={isRepeating}
                />
            )}
            <SpringScrollbars
                className={styles.customScroll}
                id={SCROLL_ID}
                autoHide
                onUpdate={onUpdate}
                ref={setScrollbarRef}
                renderView={getRenderedView}
            >
                {renderSteps()}
            </SpringScrollbars>
        </div>
    );
};

export default ReplaySidebar;
